import React, { useRef } from 'react';
import { useNavigateAndScroll } from '../Hooks/useNavigateAndScroll';

const HIPAAPrivacyPolicy = () => {
  const barRef = useRef(null);
  const navigateAndScroll = useNavigateAndScroll();

  const sections = [
    { id: 'overview', title: 'Overview' },
    { id: 'uses', title: 'How We Use & Disclose PHI' },
    { id: 'other-uses', title: 'Other Uses & Disclosures' },
    { id: 'rights', title: 'Your Rights' },
    { id: 'responsibilities', title: 'Our Responsibilities' },
    { id: 'changes', title: 'Changes to This Notice' },
    { id: 'questions', title: 'Questions or Complaints' },
  ];

  const handleScroll = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>
      <div className="relative w-auto z-1 mt-[-10px]">
        {/* <img
          className="w-full h-auto rounded-lg shadow-lg mt-2"
          src="https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/privacy-banner.webp"
          alt="Privacy and confidentiality"
        /> */}
      </div>

      <div
        ref={barRef}
        className="sticky top-0 flex flex-wrap gap-2 justify-around bg-custom-blue text-white z-50 px-2 py-2"
      >
        {sections.map((section) => (
          <button
            key={section.id}
            className="p-1 text-sm cursor-pointer grow-text"
            onClick={() => handleScroll(section.id)}
          >
            {section.title}
          </button>
        ))}
      </div>

      <div className="w-full px-4 py-8 sm:px-6 lg:px-64">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">
          HIPAA Privacy Policy
        </h1>
        <hr className="border-t-2 border-blue-500 mb-6" />

        {/* Overview */}
        <section id="overview" className="space-y-4 mb-10">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Your Privacy Matters to Us
          </h2>
          <p className="text-gray-600">
            At Central Illinois Pediatric Associates, PLLC, your child’s privacy
            and health information are very important to us. We follow strict
            federal and state laws, including the Health Insurance Portability
            and Accountability Act (HIPAA), to protect your personal and medical
            information. The details below explain how we use, share, and
            safeguard your information, and describe your rights regarding your
            child’s health records.
          </p>
          <p className="text-gray-600">
            We understand that your child’s medical information is personal. We
            are dedicated to maintaining confidentiality and using your
            protected health information (PHI) responsibly and in compliance
            with HIPAA and other applicable laws.
          </p>
        </section>

        {/* Uses and Disclosures */}
        <section id="uses" className="mb-10">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            How We May Use and Disclose Health Information
          </h2>
          <ul className="list-disc list-outside ml-5 text-gray-600 space-y-2">
            <li>
              <span className="font-semibold">For Treatment:</span> We may use or
              share your child’s health information to provide, coordinate, or
              manage medical care. This may include communication with
              specialists, hospitals, laboratories, pharmacies, or other
              healthcare providers involved in your child’s treatment.
            </li>
            <li>
              <span className="font-semibold">For Payment:</span> We may use and
              disclose PHI to bill and collect payment for services rendered,
              including sharing information with insurance companies or other
              payers.
            </li>
            <li>
              <span className="font-semibold">For Healthcare Operations:</span> We
              may use PHI for administrative and operational purposes, such as
              quality improvement, staff training, accreditation, or licensing.
            </li>
            <li>
              <span className="font-semibold">As Required by Law:</span> We may
              disclose information when required by federal, state, or local
              law.
            </li>
            <li>
              <span className="font-semibold">For Public Health and Safety:</span>{' '}
              We may share information to prevent or control disease, report
              child abuse or neglect, or avert a serious threat to health or
              safety.
            </li>
            <li>
              <span className="font-semibold">
                For Appointment Reminders and Health Information:
              </span>{' '}
              We may contact you about upcoming appointments, test results, or
              information about treatment options and services that may benefit
              your child.
            </li>
          </ul>
        </section>

        {/* Other Uses */}
        <section id="other-uses" className="mb-10">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Other Uses and Disclosures
          </h2>
          <p className="text-gray-600">
            We will not use or disclose your child’s health information for any
            purpose not described in this notice unless you give us written
            authorization. You may revoke your authorization at any time, in
            writing, except to the extent that action has already been taken.
          </p>
        </section>

        {/* Rights */}
        <section id="rights" className="mb-10">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Your Rights Regarding Health Information
          </h2>
          <ul className="list-disc list-outside ml-5 text-gray-600 space-y-2">
            <li>Access and receive a copy of your child’s medical record.</li>
            <li>
              Request corrections to health information you believe is
              inaccurate or incomplete.
            </li>
            <li>
              Request restrictions on certain uses or disclosures of PHI (though
              we may not be required to agree).
            </li>
            <li>
              Request confidential communications, such as receiving mail at an
              alternate address.
            </li>
            <li>
              Receive a record of disclosures we have made of your child’s PHI.
            </li>
            <li>Request a paper copy of this notice at any time.</li>
          </ul>
        </section>

        {/* Responsibilities */}
        <section id="responsibilities" className="mb-10">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Our Responsibilities
          </h2>
          <ul className="list-disc list-outside ml-5 text-gray-600 space-y-2">
            <li>
              We are required by law to maintain the privacy and security of
              your child’s protected health information.
            </li>
            <li>
              We will notify you promptly if a breach occurs that may compromise
              that information.
            </li>
            <li>
              We will follow the duties and privacy practices described in this
              notice and provide updates if they change.
            </li>
            <li>
              We will not use or share your PHI in any way not described here
              without your written permission.
            </li>
          </ul>
        </section>

        {/* Changes */}
        <section id="changes" className="mb-10">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Changes to This Notice
          </h2>
          <p className="text-gray-600">
            We may update this notice as needed to reflect changes in laws or
            our practices. Updated versions will always be available on our
            website and in our office.
          </p>
        </section>

        {/* Questions */}
        <section id="questions" className="mb-10">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Questions or Complaints
          </h2>
          <p className="text-gray-600 mb-2">
            If you have any questions about this notice or believe your privacy
            rights have been violated, please contact:
          </p>
          <div className="rounded-lg border p-4 text-gray-700">
            <p className="font-semibold">Privacy Officer</p>
            <p>Central Illinois Pediatric Associates, PLLC</p>
            <p>
              Email:{' '}
              <a
                className="text-blue-600 hover:underline"
                href="mailto:contactus@yourkidsmd.com"
              >
                contactus@yourkidsmd.com
              </a>
            </p>
            <p>
              Phone:{' '}
              <a
                className="text-blue-600 hover:underline"
                href="tel:+13096631011"
              >
                (309) 663-1011
              </a>
            </p>
          </div>
          <p className="text-gray-600 mt-4">
            Thank you for trusting Central Illinois Pediatric Associates, PLLC
            with your child’s care. Protecting your family’s privacy is one of
            our highest priorities.
          </p>
        </section>
      </div>
    </div>
  );
};

export default HIPAAPrivacyPolicy;
